import React, { useState } from 'react';
import SharedWorkDescriptionSection from '../../../shared/components/WorkDescriptionSection';
import { getYrkesgruppColor } from '../../../../utils/yrkesgruppColors';
import { filtreraPaYrkesgrupp, ADMIN_YRKESGRUPPER } from '../../../../utils/arbetsbeskrivningRum';

// Sorteringsordning för yrkesgrupp-legenden (matchar BuilderPUView)
const YRKESGRUPP_ORDER = [
  'Arbetsledning', 'Framkörning', 'Bystning', 'Grovstäd', 'Sophantering',
  'Rivning',
  'Byggnation', 'Snickeri', 'Fönstermontage', 'Fönsterdörrmontage', 'Grundarbete', 'Stomarbete', 'Murarbete', 'Takarbete', 'Plåtarbete',
  'Underarbete', 'Finisharbete',
  'Nota', 'Slipa (lack)', 'Slipa (hårdvax)', 'Slipa (luta)', 'Slipa (grov)', 'Fiskbensparkett', 'Massiv parkett (fallande L)', 'Mattläggning',
  'Tätskikt', 'Flytspackling', 'Plattsättare',
  'Elektriker',
  'VVS-installatör', 'VVS-installatör (ej ROT)',
  'Gas-installatör', 'Mark', 'Öppen grupp 1', 'Öppen grupp 2',
  'Pumpbil', 'Ställning', 'Kranbil', 'Markanbud', 'Öppen grupp 1 (Ej ROT)', 'Öppen grupp 2 (Ej ROT)',
  'Försäkring',
  'Finstad', 'Besiktningsman'
];

const buildLegendItems = (arbetsbeskrivning) => {
  if (!Array.isArray(arbetsbeskrivning)) return [];
  const set = new Set();
  arbetsbeskrivning.forEach(item => {
    const yg = item?.yrkesgrupp;
    if (!yg || yg === 0 || yg === '0') return;
    if (item.isMainHeader || item.isSubHeader || item.isEmpty) return;
    if (!getYrkesgruppColor(yg)) return;
    set.add(yg);
  });
  const all = Array.from(set);
  const hasAdmin = all.some(yg => ADMIN_YRKESGRUPPER.includes(yg));
  const nonAdmin = all
    .filter(yg => !ADMIN_YRKESGRUPPER.includes(yg))
    .sort((a, b) => {
      const ia = YRKESGRUPP_ORDER.indexOf(a);
      const ib = YRKESGRUPP_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  const items = [];
  if (hasAdmin) {
    items.push({ key: 'admin', label: 'Admin', color: getYrkesgruppColor('Arbetsledning') });
  }
  nonAdmin.forEach(yg => {
    items.push({ key: yg, label: yg, color: getYrkesgruppColor(yg) });
  });
  return items;
};

/**
 * WorkDescriptionSection - Wrapper för Offert som använder shared-komponenten
 * Hanterar Offert-specifik data-struktur och översätter till shared-komponentens format
 */
const WorkDescriptionSection = ({
  offert,
  isExpanded,
  onToggle,
  showYrkesgruppColors = false,
  onToggleYrkesgruppColors,
  canFilterByYrkesgrupp = false,
  showSectionCostsOverride,
  onToggleSectionCosts,
  savingSectionCosts = false
}) => {
  // Aktiva yrkesgrupp-filter (multi-select). Tom Set = visa alla.
  // Endast tillgängligt för canFilterByYrkesgrupp = true (alla utom kund).
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const handleLegendClick = (key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const hasActiveFilter = selectedKeys.size > 0;
  // Om ingen arbetsbeskrivning finns, visa ingenting
  if (!offert?.puData?.workDescription) {
    return null;
  }

  // Extrahera arbetsbeskrivning från offert-objektet
  const puDataWrapper = offert.puData?.workDescription;
  let arbetsbeskrivning = null;
  let sectionCosts = {};
  let optionSectionCosts = {};
  let showSectionCostsFromData = false;
  let showRowPrices = false;

  // Hantera både enkel och dubbel nesting (olika datastrukturer)
  if (puDataWrapper?.excelData?.sheets?.[0]?.arbetsbeskrivning) {
    arbetsbeskrivning = puDataWrapper.excelData.sheets[0].arbetsbeskrivning;
    sectionCosts = puDataWrapper.excelData.sheets[0].sectionCosts || {};
    optionSectionCosts = puDataWrapper.excelData.sheets[0].optionSectionCosts || {};
    showSectionCostsFromData = puDataWrapper.excelData.sheets[0].showSectionCosts || false;
    showRowPrices = puDataWrapper.excelData.sheets[0].showRowPrices || false;
  } else if (puDataWrapper?.sheets?.[0]?.arbetsbeskrivning) {
    arbetsbeskrivning = puDataWrapper.sheets[0].arbetsbeskrivning;
    sectionCosts = puDataWrapper.sheets[0].sectionCosts || {};
    optionSectionCosts = puDataWrapper.sheets[0].optionSectionCosts || {};
    showSectionCostsFromData = puDataWrapper.sheets[0].showSectionCosts || false;
    showRowPrices = puDataWrapper.sheets[0].showRowPrices || false;
  }

  // Använd override om tillgänglig, annars data från puData
  const showSectionCosts = showSectionCostsOverride !== undefined ? showSectionCostsOverride : showSectionCostsFromData;

  // Om vi har strukturerad arbetsbeskrivning, använd shared-komponenten
  if (arbetsbeskrivning && arbetsbeskrivning.length > 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        {/* Färglegend — visas när färgkodning är aktiv. Klickbar för att
            filtrera arbetsbeskrivningen om canFilterByYrkesgrupp=true. */}
        {showYrkesgruppColors && (() => {
          const legend = buildLegendItems(arbetsbeskrivning);
          if (legend.length === 0) return null;
          return (
            <div className="px-6 pt-4 pb-2 border-b border-gray-100">
              <div className="flex flex-wrap gap-x-3 gap-y-1.5 items-center">
                {legend.map(item => {
                  const isActive = selectedKeys.has(item.key);
                  const baseClass = 'flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-colors';
                  if (canFilterByYrkesgrupp) {
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleLegendClick(item.key)}
                        className={`${baseClass} ${
                          isActive
                            ? 'bg-gray-100 ring-1 ring-gray-300'
                            : 'hover:bg-gray-50'
                        }`}
                        title={isActive ? 'Klicka igen för att avmarkera' : `Filtrera på ${item.label}`}
                      >
                        <div
                          className="w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: item.color?.hex || '#9ca3af' }}
                        />
                        <span className={`whitespace-nowrap ${isActive ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                          {item.label}
                        </span>
                      </button>
                    );
                  }
                  return (
                    <div key={item.key} className={baseClass}>
                      <div
                        className="w-3 h-3 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: item.color?.hex || '#9ca3af' }}
                      />
                      <span className="text-gray-600 whitespace-nowrap">{item.label}</span>
                    </div>
                  );
                })}
                {hasActiveFilter && canFilterByYrkesgrupp && (
                  <button
                    type="button"
                    onClick={() => setSelectedKeys(new Set())}
                    className="text-xs text-leadme-pink hover:text-leadme-pink-dark underline ml-2"
                  >
                    Visa alla
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Toolbar med togglar (sektionspriser för BK + färgkodning för alla utom kund) */}
        {(onToggleSectionCosts || onToggleYrkesgruppColors) && (
          <div className="px-6 pt-4 pb-2 border-b border-gray-100 flex flex-wrap items-center justify-end gap-x-6 gap-y-2">
            {onToggleYrkesgruppColors && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className="text-sm text-gray-600">Färgkodning</span>
                <button
                  type="button"
                  onClick={() => onToggleYrkesgruppColors(!showYrkesgruppColors)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    showYrkesgruppColors ? 'bg-primary' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={showYrkesgruppColors}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      showYrkesgruppColors ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </label>
            )}
            {onToggleSectionCosts && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className="text-sm text-gray-600">Visa sektionspriser</span>
                <button
                  type="button"
                  onClick={() => onToggleSectionCosts(!showSectionCosts)}
                  disabled={savingSectionCosts}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    showSectionCosts ? 'bg-primary' : 'bg-gray-300'
                  } ${savingSectionCosts ? 'opacity-50 cursor-wait' : ''}`}
                  role="switch"
                  aria-checked={showSectionCosts}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      showSectionCosts ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </label>
            )}
          </div>
        )}
        <SharedWorkDescriptionSection
          arbetsbeskrivning={
            canFilterByYrkesgrupp && hasActiveFilter
              ? filtreraPaYrkesgrupp(arbetsbeskrivning, selectedKeys)
              : arbetsbeskrivning
          }
          isCollapsed={!isExpanded}
          onToggle={onToggle}
          title="Arbetsbeskrivning"
          className=""
          sectionCosts={sectionCosts}
          optionSectionCosts={optionSectionCosts}
          showSectionCosts={showSectionCosts}
          showRowPrices={showRowPrices}
          showYrkesgruppColors={showYrkesgruppColors}
        />
      </div>
    );
  }

  // Fallback för enkel textbeskrivning eller ingen data
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <button
        onClick={onToggle}
        className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <h2 className="text-lg font-semibold text-leadme-gray-dark">Arbetsbeskrivning</h2>
        <svg
          className={`w-5 h-5 text-gray-500 transform transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isExpanded && (
        <div className="px-6 pb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            {typeof offert.puData?.workDescription === 'string' ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {offert.puData.workDescription}
              </p>
            ) : (
              <p className="text-sm text-gray-500 text-center">Ingen arbetsbeskrivning tillgänglig</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkDescriptionSection;

